"use client"

import { useState } from "react"
import useSWR, { mutate } from "swr"
import { Trash2, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ApiKeyService,
  type ApiKeyMetadata,
} from "@/services/api-key.service"
import { CreateApiKeyDialog } from "@/components/create-api-key-dialog"

const fetcher = async (): Promise<{
  success: boolean;
  data: ApiKeyMetadata[];
}> => {
  const apiKeys = await ApiKeyService.getAll();
  return { success: true, data: apiKeys };
};

export function ApiKeys() {
  const [isDeleting, setIsDeleting] = useState<string | null>(null)

  const { data, isLoading, error: swrError } = useSWR<{
    success: boolean;
    data: ApiKeyMetadata[];
  }>("api-keys", fetcher)

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this API key? This action cannot be undone.")) return

    setIsDeleting(id)
    try {
      await ApiKeyService.delete(id)
      mutate("api-keys")
    } catch (err) {
      console.error(err)
      alert("Failed to delete API key")
    } finally {
      setIsDeleting(null)
    }
  }

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    try {
      await ApiKeyService.toggleEnabled(id, enabled)
      // Optimistically update the local data
      mutate("api-keys")
    } catch (err) {
      console.error("Failed to toggle API key status:", err)
      alert("Failed to toggle API key status")
    }
  }

  if (swrError) return <div className="p-6 text-red-500">Failed to load API keys</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center px-6 pt-6">
        <div>
           {/* Header content if needed, currently managed by parent CardHeader */}
        </div>
        <CreateApiKeyDialog />
      </div>

      <div className="p-0">
        <Table>
           <TableHeader>
             <TableRow>
               <TableHead className="pl-6">Enabled</TableHead>
               <TableHead>Name</TableHead>
               <TableHead>Created</TableHead>
               <TableHead>Last Used</TableHead>
               <TableHead className="text-right pr-6">Actions</TableHead>
             </TableRow>
           </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : data?.data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  No API keys found. Create one to get started.
                </TableCell>
              </TableRow>
            ) : (
               data?.data?.map((apiKey) => (
                 <TableRow key={apiKey.id}>
                    <TableCell className="pl-6">
                      <Switch 
                        checked={apiKey.enabled}
                        onCheckedChange={(enabled) => handleToggleEnabled(apiKey.id, enabled)}
                        aria-label={`Toggle ${apiKey.name} API key`}
                      />
                    </TableCell>
                   <TableCell className="font-medium">{apiKey.name}</TableCell>
                   <TableCell>
                     {apiKey.createdAt.toLocaleDateString()}
                   </TableCell>
                   <TableCell>
                     {apiKey.lastUsed 
                       ? apiKey.lastUsed.toLocaleDateString()
                       : "Never"
                     }
                   </TableCell>
                  <TableCell className="text-right pr-6">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive/90"
                      onClick={() => handleDelete(apiKey.id)}
                      disabled={isDeleting === apiKey.id}
                    >
                      {isDeleting === apiKey.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      <span className="sr-only">Delete</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
